import type { GetServerSidePropsContext } from "next"
import { MINDTRIP_LOCALE_COOKIE } from "@/types/auth"
import { getCookie, setCookie } from "cookies-next"

import {
  addDaysToTimestamp,
  createJSDateFromTimestamp,
  now,
} from "@/lib/date-utils"
import { env } from "@/lib/env"
import { isEmbedded } from "@/lib/is-embedded"

// The mindtrip.locale cookie carries the client-resolved locale tag (e.g. "fr-FR") so
// the server can read it: getServerSideProps resolves the display locale from it and
// server-axios forwards it as X-Mindtrip-Locale. Written client-side by LocaleHandler.

// The stored locale tag, or undefined when absent. Pass `req` so cookies-next reads the
// request rather than a (missing) browser document.
export function mindtripLocaleFromRequest(
  req: GetServerSidePropsContext["req"]
): string | undefined {
  const value = getCookie(MINDTRIP_LOCALE_COOKIE, { req })
  return typeof value === "string" && value.length > 0 ? value : undefined
}

// The stored locale tag, or undefined when unset (or server-side, no document).
export function readLocaleCookie(): string | undefined {
  const value = getCookie(MINDTRIP_LOCALE_COOKIE)
  return typeof value === "string" && value.length > 0 ? value : undefined
}

// Never touch cookies in the embedded surface (partner iframe / microsite): localization
// there runs off the weglotEnabled prop, not this cookie, and we don't want to write a
// third-party cookie. Mirrors uiSettings, which falls back to localStorage when embedded.
export function setLocaleCookie(locale: string): void {
  if (isEmbedded()) return
  // Mirror the uiSettings cookie: non-httpOnly, lax, secure outside dev, long-lived.
  setCookie(MINDTRIP_LOCALE_COOKIE, locale, {
    httpOnly: false,
    sameSite: "lax",
    secure: env("NEXT_PUBLIC_DEVELOPMENT") !== "true",
    expires: createJSDateFromTimestamp(addDaysToTimestamp(now(), 365 * 10)),
  })
}
